import "./PlannerTasksWidget.scss";
import { useState } from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaCheck, FaPlayCircle, FaRegCircle, FaRegClock } from 'react-icons/fa';
import ConfirmModal from '@/components/ConfirmModal';
import type { PlannerTaskDto } from "@/types/dashboard";

interface Props {
  tasks: PlannerTaskDto[];
  className?: string;
  onTaskComplete?: (taskId: string) => Promise<void>;
}

function getProgressStatus(task: PlannerTaskDto) {
  const percentComplete = task.percentComplete ?? 0;

  if (percentComplete > 0) {
    return { label: `In progress (${percentComplete}%)`, icon: FaPlayCircle, type: "in-progress" };
  }

  return { label: "Not started", icon: FaRegCircle, type: "not-started" };
}

export default function PlannerTasksWidget({ tasks, className = "", onTaskComplete }: Props) {
  const [completing, setCompleting] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState<{ id: string; title: string } | null>(null);

  const handleCompleteClick = (taskId: string, taskTitle: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setTaskToComplete({ id: taskId, title: taskTitle });
    setShowConfirm(true);
  };

  const handleConfirmComplete = async () => {
    if (!onTaskComplete || !taskToComplete) return;

    setShowConfirm(false);
    setCompleting(taskToComplete.id);
    try {
      await onTaskComplete(taskToComplete.id);
    } catch (error) {
      console.error('Failed to complete task:', error);
    } finally {
      setCompleting(null);
      setTaskToComplete(null);
    }
  };

  const handleCancelComplete = () => {
    setShowConfirm(false);
    setTaskToComplete(null);
  };

  return (
    <div className={`planner-tasks-widget ${className}`}>
      {tasks.length === 0 && <div className="empty">No tasks assigned</div>}

      <ul className="list-unstyled">
        {tasks.slice(0, 8).map((t) => {
          const progressStatus = getProgressStatus(t);
          const ProgressIcon = progressStatus.icon;
          const due = t.dueUtc
            ? new Date(t.dueUtc).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
              })
            : null;

          return (
            <li key={t.id} className="planner-task">
              <div className="task-header">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>{progressStatus.label}</Tooltip>}
                >
                  <span
                    className={`planner-task-status planner-task-status--${progressStatus.type}`}
                    aria-label={progressStatus.label}
                  >
                    <ProgressIcon />
                  </span>
                </OverlayTrigger>
                {t.webUrl ? (
                  <a
                    href={t.webUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="planner-task-link"
                  >
                    {t.title}
                  </a>
                ) : (
                  <span className="planner-task-title">{t.title}</span>
                )}

                {onTaskComplete && (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Mark as complete</Tooltip>}
                  >
                    <Button
                      variant="link"
                      size="sm"
                      className="planner-task-complete text-success p-0"
                      onClick={(e) => handleCompleteClick(t.id, t.title, e)}
                      disabled={completing === t.id}
                      style={{ fontSize: '0.9rem' }}
                    >
                      <FaCheck />
                    </Button>
                  </OverlayTrigger>
                )}

              </div>

              {(t.bucketName || t.planTitle) && (
                <div className="meta">
                  {t.bucketName}
                  {t.bucketName && t.planTitle && " · "}
                  {t.planTitle}
                </div>
              )}

              <div className="task-footer">
                {due && <span className="due">Due {due}</span>}
                {t.lastModifiedUtc && (
                  <span className="last-updated">
                    <FaRegClock /> Updated {new Date(t.lastModifiedUtc).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <ConfirmModal
        show={showConfirm}
        title="Mark Task Complete"
        message={`Are you sure you want to mark "${taskToComplete?.title}" as complete?`}
        onConfirm={handleConfirmComplete}
        onCancel={handleCancelComplete}
      />

      <div className="planner-task-legend" aria-label="Planner task status legend">
        <span><FaRegCircle aria-hidden="true" /> Not started</span>
        <span><FaPlayCircle aria-hidden="true" /> In progress</span>
      </div>

    </div>
  );
}